/*

example tap configuration

 {
    type: 'tap', 
    tapPositions: [
      {x : 840, y : 360}
    ],
  }

  
example drag configuration

 {
    type: 'drag', 
    dragPositions: {
      from: [
        { x: 840, y: 250 },
        { x: 600, y: 250 },
      ],
      to: [
        { x: 840, y: 600 },
        { x: 600, y: 600 },
      ],
    }
  }

*/

import { ONBOARDING_INITIAL_DELAY } from '../../constants/Constants';

export class OnboardingPointer extends Phaser.GameObjects.Image {
  constructor(scene, opts = {}) {
    super(scene, 0, 0, 'shared_ui', 'ui_hand');
    this.opts = opts;
    scene.add.existing(this);
    this.setOrigin(0.1, 0.1);

    this.type = opts.type || opts.dragPositions ? 'drag' : 'tap';
    this.timeout = opts.timeout || ONBOARDING_INITIAL_DELAY;
    this.tapPositions = opts.tapPositions || false;
    this.dragPositions = opts.dragPositions || false;
    this.visible = false;
    this.enabled = false;
  }

  setTapPositions(value) {
    this.tapPositions = value;
    this.type = 'tap';
    this.clearPointer();
    this.resetTimer();
  }

  setDragPositions(value) {
    this.dragPositions = value;
    this.type = 'drag';
    this.clearPointer();
    this.resetTimer();
  }

  enable() {
    if (this.enabled) return;
    this.enabled = true;
    this.scene.input.on('pointerdown', this.handleInputDown, this);
    this.scene.input.on('gameobjectdown', this.handleInputDown, this);
    this.scene.input.on('pointerup', this.handleInputUp, this);
    this.scene.input.on('gameobjectup', this.handleInputUp, this);
    this.scene.input.on('gameout', this.handleInputUp, this);

    this.startTimer();
  }

  disable() {
    this.enabled = false;
    this.scene.input.off('pointerdown', this.handleInputDown, this);
    this.scene.input.off('pointerup', this.handleInputUp, this);
    this.scene.input.off('gameobjectup', this.handleInputUp, this);
    this.scene.input.off('gameobjectdown', this.handleInputDown, this);
    this.scene.input.off('gameout', this.handleInputUp, this);
    this.clearTimer();
    this.clearPointer();
  }

  handleInputDown() {
    if (this.timer) {
      this.timer.paused = true;
    }
    this.clearPointer();
  }

  handleInputUp() {
    if (this.timer) {
      this.timer.paused = false;
    } else this.startTimer();
  }

  startTimer() {
    if (this.timer || this.timeline) return;
    this.timer = this.scene.time.delayedCall(this.timeout, this.startPointer, null, this);
  }

  resetTimer() {
    if (this.timer) {
      this.timer.elapsedTime = 0;
    } else this.startTimer();
  }

  clearTimer() {
    if (this.timer) {
      this.timer.remove();
      this.timer = null;
    }
  }

  startPointer() {
    this.alpha = 0;
    this.scale = 1;
    this.visible = true;
    this.scene.children.bringToTop(this);
    switch (this.type) {
      case 'drag':
        this.startDragPointer();
      default:
        this.startTapPointer();
    }
  }

  startTapPointer() {
    this.clearTimer();
    if (!this.tapPositions || this.timeline) return;
    this.timeline = this.scene.tweens.createTimeline();
    this.tapPositions.forEach((position) => {
      this.timeline.add({
        targets: this,
        alpha: 1,
        duration: 250,
        onStart: this.updatePosition,
        onStartScope: this,
        onStartParams: [position.x, position.y],
      });
      this.timeline.add({
        targets: this,
        scale: 0.75,
        yoyo: true,
        duration: 250,
        repeat: 1,
      });
      this.timeline.add({
        targets: this,
        alpha: 0,
        duration: 250,
        completeDelay: 250,
      });
    });

    this.timeline.completeDelay = 1000;
    this.timeline.on(
      'complete',
      () => {
        this.timeline = null;
        this.startTapPointer();
      },
      this
    );
    this.timeline.play();
  }

  startDragPointer() {
    this.clearTimer();

    if (!this.dragPositions?.to || !this.dragPositions?.from || this.timeline) return;

    this.alpha = 0;
    this.scale = 1;
    this.visible = true;
    this.timeline = this.scene.tweens.createTimeline();
    this.dragPositions.from.forEach((from) => {
      this.dragPositions.to.forEach((to) => {
        const distance = Math.sqrt(Math.pow(to.x - from.x, 2) + Math.pow(to.y - from.y, 2));
        this.timeline.add({
          targets: this,
          alpha: 1,
          duration: 250,
          onStart: this.updatePosition,
          onStartScope: this,
          onStartParams: [from.x, from.y],
        });
        this.timeline.add({
          targets: this,
          x: to.x,
          y: to.y,
          duration: Math.max(150, distance * 1.25),
        });
        this.timeline.add({
          targets: this,
          alpha: 0,
          duration: 250,
          completeDelay: 250,
        });
      });
    });
    this.timeline.completeDelay = 1000;
    this.timeline.on(
      'complete',
      () => {
        this.timeline = null;
        this.startDragPointer();
      },
      this
    );
    this.timeline.play();
  }

  clearPointer() {
    if (this.timeline) {
      this.timeline.stop();
      this.timeline = null;
    }
    this.visible = false;
  }

  updatePosition(a, b, x, y) {
    this.setPosition(x, y);
  }

  destroy() {
    this.disable();
    super.destroy();
  }
}
